/* eslint-disable @nx/enforce-module-boundaries */
import { CommonModule, JsonPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BaseListItems,
  ConfirmDialogService,
  FilterParams,
  LoaderIndicatorDirective,
  CardTableComponent,
  SearchComponent,
  CustomPaginator,
} from '@t-llevo/common-ui';

import { apiDriverRoutes as apiRoutes } from '../../../api/routes/api-driver-routes';
import { DriverAddComponent } from '../driver-add/driver-add.component';
import { MatNativeDateModule } from '@angular/material/core';
import { DriverEditComponent } from '../driver-edit/driver-edit.component';
import { DriverService } from '../../../api/services/driver.service';
import { Driver } from '../../../common/models/Driver';
import { DriverDetailTabComponent } from '../driver-detail-tab/driver-detail-tab.component';
import { UserChangePasswordComponent } from '../../../user/components/user-change-password/user-change-password.component';
import { MatSelectModule } from '@angular/material/select';
import { VehicleTypesService } from 'admin-drivers/src/app/api/services/vehicle-types.service';
import { last, map, merge, take, tap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SelectFilterComponent } from 'admin-drivers/src/app/common/components/select-filter/select-filter.component';
import { DriverResponse } from 'admin-drivers/src/app/api/models/responses/DriverResponse';
import { ResponseData } from '@t-llevo/util';

@Component({
  selector: 't-llevo-driver-list',
  standalone: true,
  imports: [
    CardTableComponent,
    SearchComponent,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatMenuModule,
    MatSelectModule,
    LoaderIndicatorDirective,
    MatDialogModule,
    MatTooltipModule,
    MatNativeDateModule,
    DriverDetailTabComponent,
    SelectFilterComponent,
    JsonPipe,
  ],
  templateUrl: './driver-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ConfirmDialogService,
    { provide: MatPaginatorIntl, useValue: CustomPaginator() },
  ],
})
export class DriverListComponent
  extends BaseListItems<Driver, ResponseData<DriverResponse>>
  implements OnDestroy, AfterViewInit
{
  @Input() searchTerm = '';
  displayedColumns: string[] = [
    'name',
    'lastname',
    'dni',
    'vehicle',
    'registrationNumber',
    'actions',
  ];
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('vehicleTypeFilter') vehicleTypeFilter!: SelectFilterComponent;
  @ViewChild('searchFilter') searchFilter!: SearchComponent;

  showDrawer = false;
  destroyRef = inject(DestroyRef);

  protected vehicleTypeSrv = inject(VehicleTypesService);
  vehicleTypes = toSignal(
    this.vehicleTypeSrv
      .getVehicleTypes()
      .pipe(take(1))
      .pipe(
        map((data) => {
          return data.map((item) => {
            return {
              id: item.name,
              value: item.name,
            };
          });
        }),
      ),
  );

  constructor(
    public dialog: MatDialog,
    private driverService: DriverService,
  ) {
    super(driverService, apiRoutes.routeDrivers);
  }

  ngAfterViewInit(): void {
    this.sort.sortChange
      .pipe(takeUntilDestroyed(this.destroyRef), last())
      .subscribe(() => (this.paginator.pageIndex = 0));
    merge(this.paginator.page, this.sort.sortChange)
      .pipe(tap(() => this.loadItems(this._getFiltersParams())))
      .subscribe();
    this.loadItems(this._getFiltersParams());
  }

  ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.complete();
  }

  openAddDriverDialog() {
    this.dialog
      .open(DriverAddComponent, {
        width: '90%',
        maxHeight: '90%',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((data: boolean) => {
        if (data) {
          this.loadItems(this._getFiltersParams());
        }
      });
  }

  openEditDriverDialog(driverData: Driver) {
    this.dialog
      .open(DriverEditComponent, {
        data: driverData,
        width: '70%',
        maxHeight: '90%',
        disableClose: true,
        panelClass: 'bg-gray',
      })
      .afterClosed()
      .subscribe((data: boolean) => {
        if (data) {
          this.loadItems(this._getFiltersParams());
        }
      });
  }

  openChangePasswordDialog(driverData: Driver) {
    this.dialog.open(UserChangePasswordComponent, {
      data: driverData.user,
      width: '50%',
      maxHeight: '70%',
      disableClose: true,
    });
  }

  openDeleteDialog(driver: Driver) {
    const config = {
      title: `¿Desea eliminar el elemento?`,
      message: 'El elemento se eliminará de forma permanente',
      cancelText: 'CANCELAR',
      confirmText: 'SI, DESEO ELIMINAR',
    };
    this.confirmDialogSrv.open(config).subscribe((dataDialog) => {
      if (dataDialog) {
        this.driverService.deleteDriver(driver).subscribe({
          next: () => {
            this.notifySrv.dispatch('Conductor eliminado', 'success');
            this.loadItems();
          },
        });
      }
    });
  }

  _getFiltersParams(defaultField = 'name'): FilterParams {
    // const activeField =
    //   this.sort && this.sort.active ? this.sort.active : defaultField;
    const params: FilterParams = {
      page: this.paginator ? this.paginator.pageIndex + 1 : 1,
      limit: this.paginator ? this.paginator.pageSize : 10,
      // ordering:
      //   this.sort && this.sort.direction === 'asc'
      //     ? activeField
      //     : `-${activeField}`,
    };
    if (this.searchParam) {
      params['search'] = this.searchParam;
    }
    if (this.vehicleTypeFilter.selectedItems.length > 0) {
      params['vehicleType'] = this.vehicleTypeFilter.selectedItems.toString();
    }

    return params;
  }

  override onSearch(ev: string): void {
    if (this.searchParam !== ev || this.searchParam === '') {
      this.searchParam = ev;
      this.loadItems(this._getFiltersParams());
    }
  }

  detail(item: Driver): void {
    this.showDrawer = !this.showDrawer;
    this.driverService.getItem('', item.id, false);
  }

  openedDrawerChange(open: boolean) {
    this.showDrawer = open;
  }

  filter() {
    this.loadItems(this._getFiltersParams());
  }
}
